import React from "react";
import { Link } from "react-router-dom";
import { Button, Grid, Header, Message, Table } from "semantic-ui-react";
import { processRawLimitsAndAccounting } from "../../../../actions/accounts/accounts";
import { amRoutes, availableSystems, pencilerRoles } from "../../../../config/config";
import { messages } from "../../../../config/constants";
import { history } from "../../../../store";
import { ContactInformation, Organization } from "../../../../types/organizations";
import { PencilerStartProjectsStatus } from "../../../../types/pencilerStartProjects";
import { User, UserClaims } from "../../../../types/users";
import { textInputRender } from "../../../../utils/components";
import { validateEmailString } from "../../../../utils/formValidation";
import { exists, usersWithRole } from "../../../../utils/utils";
import PrivateRoute from "../../../auth/PrivateRoute";
import DismissibleMessage from "../../../common/DismissibleMesage";
import FormModal from "../../../common/FormModal";
import TableRow from "../../../common/TableRow";
import SubscriptionField from "../../../subscriptions/SubscriptionField";
import SubscriptionsModal from "../../../subscriptions/SubscriptionsModal";
import DatabaseUsageProgressBar from "./DatabaseUsageProgressBar";
import ManageOrganizationAndContact from "./ManageOrganizationAndContact";
import RegionInfoGrid from "./RegionInfoGrid";
import SimulationRunsProgressBar from "./SimulationRunsProgressBar";

type SubscriptionsProps = {
  organization: Organization | Record<string, never>;
  user: UserClaims | Record<string, never>;
  canEdit: boolean;
  canRemove: boolean;
  canManage: boolean;
  canAdd: boolean;
  users: User[];
  canViewRuns: boolean;
  limitsAndAccounting: ReturnType<typeof processRawLimitsAndAccounting> | Record<string, never>;
  userSystems: string[];
  showSuccessMessage: boolean;
  submitSucceededMessageHeader: string;
  submitSucceededMessageContent: string;
  editOrganization: (organization: Partial<Organization>) => any;
  editOrganizationContact: (id: number, contact: ContactInformation) => any;
  removeOrganizationsContact: (id: number) => any;
  createOrganizationContact: (contact: ContactInformation) => any;
  startProjects: PencilerStartProjectsStatus | Record<string, never>;
  gettingStartProjects: boolean;
  getStartProjectsSuccess: boolean;
  getStartProjectsError: boolean;
  getStartProjectsErrorMessage: string;
  disabled: boolean;
  isExternal: boolean;
};

class Subscriptions extends React.PureComponent<SubscriptionsProps> {
  _handleChangeSubscriptionClick = () =>
    this._canManageSubscription() ? history.push(amRoutes.managementSubscriptionAdd) : null;

  _canManageSubscription = () =>
    this.props.user.roles?.some(role => pencilerRoles.canManageSubscription.includes(role.code));

  _unauthorizedManagementMessage = (): any => {
    // Not implemented
  };
  _renderSubscriptionRow = () => {
    if (this.props.gettingStartProjects || this.props.getStartProjectsError) {
      return null;
    }
    return (
      <TableRow
        title="Subscription and Usage"
        value={[
          `${this.props.startProjects.used}/`,
          <strong key="[fix_unique_key_value]">
            {this.props.startProjects.isUnlimited ? "∞" : this.props.startProjects.total}
          </strong>,
          " Projects"
        ]}
        linkTitle="Manage"
        onItemClick={this._handleChangeSubscriptionClick}
        linkDisabled={!this._canManageSubscription()}
        infoContent={this._canManageSubscription() ? null : this._unauthorizedManagementMessage()}
      />
    );
  };

  _renderSubscription = () => {
    if (this.props.getStartProjectsError) {
      return (
        <Grid.Row columns={1} style={{ maxWidth: 750 }}>
          <Message negative>
            <Message.Header>Error fetching subscription and limits.</Message.Header>
            <p>{this.props.getStartProjectsErrorMessage}</p>
          </Message>
        </Grid.Row>
      );
    }
    return (
      <Grid.Row columns={1} style={{ maxWidth: 750 }}>
        <Grid.Column width={16} textAlign="left">
          <SubscriptionField
            value={this.props.startProjects.used}
            total={
              typeof this.props.startProjects.total === "number"
                ? this.props.startProjects.total
                : 0
            }
            infiniteTotal={this.props.startProjects.isUnlimited}
            label="Current Usage"
          />
        </Grid.Column>
      </Grid.Row>
    );
  };

  _renderSubscriptionUsers = () => {
    const cellStyle = { border: 0, paddingTop: 0, paddingBottom: 0, paddingLeft: 0 };
    const renderTableCell = (input: string | number) => (
      <Table.Cell style={cellStyle}>{input}</Table.Cell>
    );
    const rows = [
      { label: "Administrator: ", value: usersWithRole(this.props.users, "ADMIN").length },
      { label: "Modeler: ", value: usersWithRole(this.props.users, "MODELER").length },
      { label: "Viewer: ", value: usersWithRole(this.props.users, "VIEWER").length }
    ];
    return (
      <>
        <Header as="h4">Subscription users</Header>
        <Table style={{ border: 0 }} celled collapsing>
          <Table.Body>
            {rows.map((row, index) => (
              <Table.Row key={index}>
                {renderTableCell(row.label)}
                {renderTableCell(row.value)}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </>
    );
  };
  _renderRegionModules = () => (
    <div>
      <Header as="h4">Subscription Add-Ons</Header>
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Description</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {this.props.user.selectedRegion?.modules !== undefined &&
          this.props.user.selectedRegion.modules.length > 0 ? (
            this.props.user.selectedRegion.modules.map(module => (
              <Table.Row key={module}>
                <Table.Cell>
                  {module === "tnc" ? "TNC Conservation Module" : module.toUpperCase()}
                </Table.Cell>
              </Table.Row>
            ))
          ) : (
            <Table.Row active>
              <Table.Cell> None </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </div>
  );

  _renderPencilerSubscription = () => (
    <div>
      <Header as="h2">Penciler</Header>
      <Grid textAlign="center" style={{ height: "100%" }} verticalAlign="top">
        {this._renderSubscriptionRow()}
        {this._renderSubscription()}
      </Grid>
    </div>
  );

  _renderUrbanCanvasSubscription = () => (
    <div style={{ marginBottom: "4%" }}>
      <Header as="h5" style={{ margin: 0 }}>
        {this.props.user.selectedRegion?.subscription?.lengthMonths === 12
          ? "Annual Subscriber"
          : "Trial"}
      </Header>
      <Header as="h4">Model</Header>
      <RegionInfoGrid
        id={this.props.user.selectedRegion?.id}
        baseYear={this.props.user.selectedRegion?.baseYear}
        template={this.props.user.selectedRegion?.modelType}
        localProjection={this.props.user.selectedRegion?.srs}
      />
      {this._renderRegionModules()}
      {this._renderSubscriptionUsers()}
      {this.props.canManage && (
        <Link className="link" to={amRoutes.managementUsers}>
          Manage
        </Link>
      )}

      <Header as="h3">Subscription limits and data usage</Header>
      {this.props.canViewRuns && exists(this.props.limitsAndAccounting) && (
        <>
          <Header as="h4">Simulation Runs</Header>
          <SimulationRunsProgressBar
            shortSimulationRunsLimit={this.props.limitsAndAccounting.shortSimulationRuns.total}
            shortSimulationRunsValue={this.props.limitsAndAccounting.shortSimulationRuns.value}
            longSimulationRunsLimit={this.props.limitsAndAccounting.longSimulationRuns.total}
            longSimulationRunsValue={this.props.limitsAndAccounting.longSimulationRuns.value}
          />
        </>
      )}
      <Header as="h4">Database Usage</Header>
      {exists(this.props.limitsAndAccounting) ? (
        <DatabaseUsageProgressBar
          databaseSize={parseInt(this.props.limitsAndAccounting.database.total)}
          databaseUsage={parseInt(this.props.limitsAndAccounting.database.value)}
        />
      ) : null}
    </div>
  );

  render() {
    return (
      <>
        <PrivateRoute
          path={amRoutes.managementSubscriptionAdd}
          render={(props: any) => <SubscriptionsModal {...props} />}
        />
        <Header as="h3">Subscription Contact Information</Header>
        {this.props.isExternal && (
          <DismissibleMessage
            className={messages.hasExternalSubscription.className}
            content={messages.hasExternalSubscription.content}
          />
        )}
        {this.props.canEdit && (
          <DismissibleMessage
            className="warning"
            content="Subscription contact information is
          visible to all user roles in your subscription."
          />
        )}
        <Header as="h4">Managing Organization</Header>
        <ManageOrganizationAndContact
          editOrganization={this.props.editOrganization}
          editOrganizationContact={this.props.editOrganizationContact}
          removeOrganizationsContact={this.props.removeOrganizationsContact}
          organizationEntity={this.props.organization}
          organizationContacts={this.props.organization.contacts.sort(
            (contactA, contactB) => contactA.id - contactB.id
          )}
          mainRegionName={this.props.user.selectedRegion?.name}
          canEdit={this.props.canEdit}
          canRemove={this.props.canRemove}
          disabled={this.props.disabled}
        />
        {this.props.canAdd && (
          <FormModal
            size="mini"
            modalHeader="Add contact"
            fields={[
              {
                name: "name",
                render: textInputRender("Name", "Name...", true),
                required: true
              },
              {
                name: "jobTitle",
                render: textInputRender("Job title", "Job title...", true),
                required: true
              },
              {
                name: "email",
                render: textInputRender("Email", "example@email.com", true),
                required: true,
                validators: [validateEmailString]
              },
              {
                name: "phone",
                render: textInputRender("Phone", "Phone...")
              }
            ]}
            onSubmit={contactInformation =>
              this.props.createOrganizationContact(contactInformation)
            }
            modalTrigger={onClick => (
              <Button basic size="mini" onClick={onClick} disabled={this.props.disabled}>
                Add contact
              </Button>
            )}
            disabled={this.props.disabled}
          />
        )}
        <Header as="h3">Subscription Type</Header>
        {this.props.userSystems.includes(availableSystems.urbancanvas) &&
          this._renderUrbanCanvasSubscription()}
        {this.props.userSystems.includes(availableSystems.penciler) &&
          this._renderPencilerSubscription()}
      </>
    );
  }
}

export default Subscriptions;
